"use client";

import React from "react";
import Button from "@/components/ui/button/Button";
import SearchableSelect from "@/components/ui/SearchableSelect";
import DatePicker from "@/components/form/date-picker";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import { Modal } from "@/components/ui/modal/index";
import { handleApiError } from "@/lib/api";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useRouter } from "next/navigation";

export const DEFAULT_FUEL_BOOK = "FUEL READING BOOK";
export const DEFAULT_FUEL_TYPE = "DIESEL EXPENSES";

/** Voucher header row — slightly tighter than line fields. */
const VOUCHER_INP =
  "!h-9 !min-h-0 !rounded-md !px-2.5 !py-1.5 !text-sm !shadow-none focus:!ring-2";
const VOUCHER_DATE_INP =
  "!h-9 !min-h-0 !rounded-md !px-2.5 !py-1.5 !pr-9 !text-sm !shadow-none focus:!ring-2";
const LINE_SEL_INP =
  "!h-9 !min-h-0 !rounded-md !px-2.5 !py-1.5 !text-sm !shadow-none focus:!ring-2";
/** Primary numeric line fields — larger text for at-a-glance reading / odometer values. */
const LINE_NUM_INP =
  "!h-9 !min-h-0 !rounded-md !px-2.5 !py-1.5 !text-sm !font-medium !tabular-nums !leading-snug !shadow-none focus:!ring-2";
const LINE_REMARK_INP =
  "!h-9 !min-h-0 !rounded-md !px-2.5 !py-1.5 !text-sm !leading-snug !shadow-none focus:!ring-2";
const COMPACT_LBL =
  "mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400";

function pickCtx(ctx, ...keys) {
  if (!ctx || typeof ctx !== "object") return "";
  for (const k of keys) {
    const v = ctx[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v);
  }
  return "";
}

/** GET detail may return `items` (write payload shape) or `lines` (read serializer). */
function fuelReadingDetailRows(detail) {
  if (!detail || typeof detail !== "object") return [];
  if (Array.isArray(detail.items) && detail.items.length) return detail.items;
  if (Array.isArray(detail.lines) && detail.lines.length) return detail.lines;
  return [];
}

function mapFuelContext(ctx) {
  const nested = ctx?.last_entry || ctx?.previous_entry || ctx?.last || {};
  const src = { ...nested, ...ctx };
  return {
    rate: pickCtx(src, "rate", "fuel_rate", "last_rate", "default_rate", "price_per_litre"),
    previous_reading: pickCtx(
      src,
      "previous_reading",
      "last_reading",
      "reading",
      "prev_reading",
      "odometer_reading"
    ),
    previous_date: pickCtx(
      src,
      "previous_reading_date",
      "previous_date",
      "last_date",
      "date"
    ),
    previous_quantity: pickCtx(src, "previous_quantity", "last_quantity", "quantity", "prev_quantity"),
  };
}

function fmtNum(v, digits = 4) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: digits > 2 ? 2 : 0,
    maximumFractionDigits: digits,
  });
}

/** Read-only display: empty dashed box, filled box, or loading. */
function ReadonlyField({ value, loading, date, digits = 4, tone = "neutral", className = "" }) {
  const hasVal = value != null && String(value).trim() !== "";
  const filledNeutral =
    "min-h-[36px] w-full max-w-full min-w-0 rounded-md border border-gray-600/45 bg-gray-900/35 px-2 py-1.5 tabular-nums text-sm font-medium leading-snug text-gray-100";
  const filledEmphasis =
    "min-h-[36px] w-full max-w-full min-w-0 rounded-md border border-emerald-900/40 bg-emerald-950/25 px-2 py-1.5 tabular-nums text-sm font-semibold leading-snug text-emerald-100/95";
  const boxEmpty =
    "min-h-[22px] w-full max-w-full min-w-0 rounded-md border border-dashed border-gray-800/50 bg-gray-950/20";
  if (loading) {
    return (
      <div
        className={`flex min-h-[36px] w-full max-w-full min-w-0 items-center rounded-md border border-gray-700/60 bg-gray-950/50 px-2 text-gray-500 ${className}`}
      >
        <span className="text-sm">…</span>
      </div>
    );
  }
  const s = hasVal ? String(value).trim() : null;
  if (s) {
    const formatted = date ? s.slice(0, 10) : fmtNum(s, digits);
    const text = formatted != null && formatted !== "" ? formatted : s;
    const base = tone === "emphasis" ? filledEmphasis : filledNeutral;
    return (
      <div className={`${base} ${date ? "font-mono text-sm tracking-tight" : ""} ${className}`}>
        {text}
      </div>
    );
  }
  return (
    <div className={`${boxEmpty} ${className}`} aria-hidden />
  );
}

function toPayloadQtyReading(v) {
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v ?? "").trim();
  return n.toFixed(4);
}

function toPayloadRate(v) {
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v ?? "").trim();
  return n.toFixed(2);
}

const itemSchema = z.object({
  vehicle_id: z.coerce.number().refine((n) => Number.isFinite(n) && n > 0, "Vehicle required"),
  driver_id: z.coerce.number().refine((n) => Number.isFinite(n) && n > 0, "Driver required"),
  line_remark: z.string().optional(),
  reading: z
    .string()
    .min(1, "Reading required")
    .refine((s) => Number.isFinite(Number(String(s).replace(/,/g, ""))), "Invalid reading"),
  quantity: z
    .string()
    .min(1, "Quantity required")
    .refine((s) => Number.isFinite(Number(String(s).replace(/,/g, ""))), "Invalid quantity"),
  rate: z
    .string()
    .min(1, "Rate is required (use last rate from context or enter manually)")
    .refine((s) => {
      const n = Number(String(s).replace(/,/g, ""));
      return Number.isFinite(n) && n > 0;
    }, "Enter a valid rate per unit"),
  previous_reading: z.string().optional(),
  previous_date: z.string().optional(),
  previous_quantity: z.string().optional(),
});

const fuelFormSchema = z.object({
  date: z.string().min(1, "Date required"),
  book_name: z.string().min(1, "Book name required"),
  fuel_type: z.string().min(1, "Fuel type required"),
  remark: z.string().optional(),
  items: z.array(itemSchema).min(1, "Add at least one line"),
});

const emptyItem = {
  vehicle_id: "",
  driver_id: "",
  line_remark: "",
  reading: "",
  quantity: "",
  rate: "",
  previous_reading: "",
  previous_date: "",
  previous_quantity: "",
};

export default function FuelReadingForm({ fuelReadingId = null }) {
  const router = useRouter();
  const qc = useQueryClient();
  const isoToday = React.useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [contextLoadingRow, setContextLoadingRow] = React.useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(fuelFormSchema),
    defaultValues: {
      date: isoToday,
      book_name: DEFAULT_FUEL_BOOK,
      fuel_type: DEFAULT_FUEL_TYPE,
      remark: "",
      items: [{ ...emptyItem }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "items" });
  const itemsWatch = watch("items");

  const { data: vehicles = [] } = useQuery({
    queryKey: ["vehicles"],
    queryFn: async () => (await rmcClient.listVehicles()).data,
  });

  const { data: drivers = [] } = useQuery({
    queryKey: ["drivers", "fuelReadingForm"],
    queryFn: async () => (await rmcClient.listDrivers()).data,
  });

  const driverOptions = React.useMemo(() => drivers.filter((d) => d.is_active !== false), [drivers]);

  const { data: detail, isFetching: isLoadingDetail } = useQuery({
    queryKey: ["fuelReading", fuelReadingId],
    queryFn: async () => (await rmcClient.getFuelReading(fuelReadingId)).data,
    enabled: fuelReadingId != null,
  });

  const { data: voucherNumberData, isLoading: isLoadingVoucherNo } = useQuery({
    queryKey: ["fuelReadingNumber"],
    queryFn: async () => (await rmcClient.getFuelReadingNumber()).data,
    enabled: fuelReadingId == null,
  });

  const fuelEditHydratedRef = React.useRef(null);

  React.useEffect(() => {
    if (fuelReadingId == null) fuelEditHydratedRef.current = null;
  }, [fuelReadingId]);

  React.useEffect(() => {
    if (fuelReadingId == null || !detail) return;
    const rid = detail.id ?? detail.pk;
    if (Number(rid) !== Number(fuelReadingId)) return;
    if (fuelEditHydratedRef.current === Number(rid)) return;
    fuelEditHydratedRef.current = Number(rid);
    const d = detail;
    const rawItems = fuelReadingDetailRows(d);
    reset({
      date: String(d.date || "").slice(0, 10) || isoToday,
      book_name: d.book_name || DEFAULT_FUEL_BOOK,
      fuel_type: d.fuel_type || DEFAULT_FUEL_TYPE,
      remark: d.remark || "",
      items:
        rawItems.length > 0
          ? rawItems.map((it) => ({
              vehicle_id: it.vehicle_id ?? it.vehicle?.id ?? "",
              driver_id: "",
              line_remark: it.remark || it.line_remark || "",
              reading: it.reading != null ? String(it.reading) : "",
              quantity: it.quantity != null ? String(it.quantity) : "",
              rate: pickCtx(it, "rate", "fuel_rate"),
              previous_reading: pickCtx(it, "previous_reading", "last_reading"),
              previous_date: pickCtx(
                it,
                "previous_date",
                "last_date",
                "previous_reading_date"
              ),
              previous_quantity: pickCtx(it, "previous_quantity", "last_quantity"),
            }))
          : [{ ...emptyItem }],
    });
  }, [fuelReadingId, detail, reset, isoToday]);

  React.useEffect(() => {
    if (fuelReadingId == null || !detail || driverOptions.length === 0) return;
    const rawItems = fuelReadingDetailRows(detail);
    rawItems.forEach((it, idx) => {
      const dn = String(it.driver_name || "").trim();
      if (!dn) return;
      const driverMatch = driverOptions.find(
        (dr) => String(dr.name || "").trim().toLowerCase() === dn.toLowerCase()
      );
      if (driverMatch?.id) setValue(`items.${idx}.driver_id`, driverMatch.id);
    });
  }, [fuelReadingId, detail, driverOptions, setValue]);

  const voucherLabel = React.useMemo(() => {
    if (fuelReadingId == null) {
      if (isLoadingVoucherNo) return "…";
      const vn =
        voucherNumberData?.next_voucher_no ??
        voucherNumberData?.data?.next_voucher_no ??
        pickCtx(voucherNumberData || {}, "next_voucher_no");
      return vn || "—";
    }
    if (isLoadingDetail) return "…";
    if (!detail) return "—";
    return (
      pickCtx(detail, "voucher_no", "voucher_number", "number") ||
      (detail.id != null ? String(detail.id) : "—")
    );
  }, [fuelReadingId, isLoadingDetail, isLoadingVoucherNo, detail, voucherNumberData]);

  const applyVehicleContext = async (rowIndex, vehicleId) => {
    const vid = Number(vehicleId);
    if (!Number.isFinite(vid) || vid <= 0) {
      setValue(`items.${rowIndex}.rate`, "");
      setValue(`items.${rowIndex}.previous_reading`, "");
      setValue(`items.${rowIndex}.previous_date`, "");
      setValue(`items.${rowIndex}.previous_quantity`, "");
      return;
    }
    setContextLoadingRow(rowIndex);
    try {
      const res = await rmcClient.getFuelReadingContext(vid);
      const ctx = res?.data ?? res;
      const m = mapFuelContext(ctx);
      setValue(`items.${rowIndex}.rate`, m.rate);
      setValue(`items.${rowIndex}.previous_reading`, m.previous_reading);
      setValue(`items.${rowIndex}.previous_date`, m.previous_date);
      setValue(`items.${rowIndex}.previous_quantity`, m.previous_quantity);
    } catch {
      toast.error("Could not load previous fuel reading for this vehicle.");
      setValue(`items.${rowIndex}.rate`, "");
      setValue(`items.${rowIndex}.previous_reading`, "");
      setValue(`items.${rowIndex}.previous_date`, "");
      setValue(`items.${rowIndex}.previous_quantity`, "");
    } finally {
      setContextLoadingRow(null);
    }
  };

  const buildPayload = (data) => ({
    date: data.date,
    book_name: data.book_name || "",
    fuel_type: data.fuel_type || "",
    remark: data.remark || "",
    items: data.items.map((it) => {
      const drv = driverOptions.find((d) => Number(d.id) === Number(it.driver_id));
      const row = {
        vehicle_id: Number(it.vehicle_id),
        driver_name: drv?.name?.trim() || "",
        reading: toPayloadQtyReading(it.reading),
        quantity: toPayloadQtyReading(it.quantity),
        rate: toPayloadRate(it.rate),
      };
      const lr = (it.line_remark || "").trim();
      if (lr) row.remark = lr;
      return row;
    }),
  });

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      fuelReadingId != null
        ? rmcClient.updateFuelReading(fuelReadingId, payload)
        : rmcClient.createFuelReading(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fuelReadings"] });
      qc.invalidateQueries({ queryKey: ["fuelReadingNumber"] });
      toast.success(fuelReadingId != null ? "Fuel reading updated" : "Fuel reading saved");
      router.push("/transport/fuel-readings");
    },
    onError: (e) => toast.error(handleApiError(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => rmcClient.deleteFuelReading(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fuelReadings"] });
      qc.invalidateQueries({ queryKey: ["fuelReadingNumber"] });
      toast.success("Fuel reading deleted");
      setDeleteConfirmOpen(false);
      router.push("/transport/fuel-readings");
    },
    onError: (e) => toast.error(handleApiError(e)),
  });

  return (
    <div className="max-w-full min-w-0 overflow-x-hidden rounded-lg border border-gray-800/90 bg-gray-950/25 p-3 sm:p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-gray-800/80 pb-3">
        <h2 className="text-base font-semibold tracking-tight text-white">Fuel reading</h2>
        <p className="text-xs text-gray-500">
          Voucher{" "}
          <span className="font-mono font-medium text-gray-200 tabular-nums">{voucherLabel}</span>
        </p>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 lg:items-end">
        <div>
          <Label className={COMPACT_LBL}>Voucher date</Label>
          <Controller
            name="date"
            control={control}
            render={({ field }) => (
              <DatePicker
                id="fuel-voucher-date"
                defaultDate={field.value}
                inputClassName={VOUCHER_DATE_INP}
                onChange={(_, dateStr) => field.onChange(dateStr || "")}
              />
            )}
          />
          {errors.date && <p className="mt-0.5 text-[11px] text-red-400">{errors.date.message}</p>}
        </div>
        <div>
          <Label className={COMPACT_LBL}>Book name</Label>
          <Controller
            name="book_name"
            control={control}
            render={({ field }) => (
              <Input className={VOUCHER_INP} {...field} value={field.value || ""} />
            )}
          />
        </div>
        <div>
          <Label className={COMPACT_LBL}>Fuel type</Label>
          <Controller
            name="fuel_type"
            control={control}
            render={({ field }) => (
              <Input className={VOUCHER_INP} {...field} value={field.value || ""} />
            )}
          />
        </div>
        <div className="sm:col-span-2 lg:col-span-1">
          <Label className={COMPACT_LBL}>Remarks</Label>
          <Controller
            name="remark"
            control={control}
            render={({ field }) => (
              <Input
                className={VOUCHER_INP}
                {...field}
                value={field.value || ""}
                placeholder="Voucher remarks"
              />
            )}
          />
        </div>
      </div>

      <div className="mt-4 w-full min-w-0 max-w-full space-y-3">
        {fields.map((f, index) => {
          const row = itemsWatch?.[index] || {};
          const rate = Number(String(row.rate || "").replace(/,/g, ""));
          const qty = Number(String(row.quantity || "").replace(/,/g, ""));
          const prevR = Number(String(row.previous_reading || "").replace(/,/g, ""));
          const curR = Number(String(row.reading || "").replace(/,/g, ""));
          const amount = Number.isFinite(rate) && Number.isFinite(qty) ? rate * qty : NaN;
          const dist = Number.isFinite(curR) && Number.isFinite(prevR) ? curR - prevR : NaN;
          const kmPerL =
            Number.isFinite(dist) && Number.isFinite(qty) && qty > 0 ? dist / qty : null;
          const showAmount =
            Number.isFinite(rate) &&
            Number.isFinite(qty) &&
            qty > 0 &&
            Number.isFinite(amount);

          return (
            <div
              key={f.id}
              className="rounded-md border border-gray-800/80 bg-gray-950/30 p-2"
            >
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2 border-b border-gray-800/60 pb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  Line {index + 1}
                </span>
                <button
                  type="button"
                  className="rounded px-1.5 py-0.5 text-xs leading-none text-red-400/90 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-40"
                  onClick={() => remove(index)}
                  disabled={fields.length <= 1}
                  aria-label="Remove line"
                >
                  ×
                </button>
              </div>

              <div className="space-y-1.5">
                <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
                  <div className="min-w-0">
                    <Label className={COMPACT_LBL}>Vehicle</Label>
                    <div className="min-w-0 w-full max-w-full">
                      <Controller
                        name={`items.${index}.vehicle_id`}
                        control={control}
                        render={({ field }) => (
                          <SearchableSelect
                            value={field.value}
                            onChange={(v) => {
                              field.onChange(v);
                              setValue(`items.${index}.driver_id`, "");
                              applyVehicleContext(index, v);
                            }}
                            options={vehicles}
                            getLabel={(v) =>
                              `${v.registration_no}${v.name || v.alias ? ` (${v.name || v.alias})` : ""}`
                            }
                            getValue={(v) => v.id}
                            disabled={contextLoadingRow === index}
                            overlayZClass="z-[100001]"
                            menuZClass="z-[100002]"
                            inputClassName={LINE_SEL_INP}
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="min-w-0">
                    <Label className={COMPACT_LBL}>Driver</Label>
                    <div className="min-w-0 w-full max-w-full">
                      <Controller
                        name={`items.${index}.driver_id`}
                        control={control}
                        render={({ field }) => (
                          <SearchableSelect
                            value={field.value}
                            onChange={field.onChange}
                            options={driverOptions}
                            getLabel={(d) =>
                              d.name
                                ? `${d.name}${d.contact_no ? ` · ${d.contact_no}` : ""}${d.employee_id ? ` (${d.employee_id})` : ""}`
                                : `Driver #${d.id}`
                            }
                            getValue={(d) => d.id}
                            overlayZClass="z-[100001]"
                            menuZClass="z-[100002]"
                            inputClassName={LINE_SEL_INP}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>

                {/* lg: one row = prev×3 + current×3; sm: two rows of 3 */}
                <div className="grid grid-cols-3 gap-x-2 gap-y-1.5 lg:grid-cols-6 lg:gap-y-1">
                  <div className="min-w-0">
                    <Label className={COMPACT_LBL}>Prev. date</Label>
                    <ReadonlyField
                      date
                      value={row.previous_date}
                      loading={contextLoadingRow === index}
                    />
                  </div>
                  <div className="min-w-0">
                    <Label className={COMPACT_LBL}>Prev. reading</Label>
                    <ReadonlyField
                      value={row.previous_reading}
                      loading={contextLoadingRow === index}
                      digits={4}
                    />
                  </div>
                  <div className="min-w-0">
                    <Label className={COMPACT_LBL}>Prev. qty</Label>
                    <ReadonlyField
                      value={row.previous_quantity}
                      loading={contextLoadingRow === index}
                      digits={4}
                    />
                  </div>
                  <div className="min-w-0">
                    <Label className={COMPACT_LBL}>Reading</Label>
                    <Controller
                      name={`items.${index}.reading`}
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="text"
                          inputMode="decimal"
                          className={`min-w-0 w-full ${LINE_NUM_INP}`}
                          {...field}
                          value={field.value ?? ""}
                        />
                      )}
                    />
                  </div>
                  <div className="min-w-0">
                    <Label className={COMPACT_LBL}>Quantity</Label>
                    <Controller
                      name={`items.${index}.quantity`}
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="text"
                          inputMode="decimal"
                          className={`min-w-0 w-full ${LINE_NUM_INP}`}
                          {...field}
                          value={field.value ?? ""}
                        />
                      )}
                    />
                  </div>
                  <div className="min-w-0">
                    <Label className={COMPACT_LBL}>Rate</Label>
                    <Controller
                      name={`items.${index}.rate`}
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="text"
                          inputMode="decimal"
                          title="Required if vehicle has no prior fuel entry; prefilled when available"
                          className={`min-w-0 w-full ${LINE_NUM_INP} ${contextLoadingRow === index ? "opacity-60" : ""}`}
                          disabled={contextLoadingRow === index}
                          {...field}
                          value={field.value ?? ""}
                          placeholder="Rate"
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-4 lg:items-end">
                  <div className="min-w-0">
                    <Label className={COMPACT_LBL}>Amount</Label>
                    <ReadonlyField
                      tone="emphasis"
                      value={showAmount ? String(amount) : null}
                      loading={false}
                      digits={2}
                    />
                  </div>
                  <div className="min-w-0">
                    <Label className={COMPACT_LBL}>km / unit</Label>
                    <ReadonlyField
                      tone="emphasis"
                      value={
                        kmPerL != null && Number.isFinite(kmPerL) && qty > 0 ? String(kmPerL) : null
                      }
                      loading={false}
                      digits={4}
                    />
                  </div>
                  <div className="min-w-0 sm:col-span-2 lg:col-span-2">
                    <Label className={COMPACT_LBL}>Remark</Label>
                    <Controller
                      name={`items.${index}.line_remark`}
                      control={control}
                      render={({ field }) => (
                        <Input
                          className={`min-w-0 w-full ${LINE_REMARK_INP}`}
                          {...field}
                          value={field.value || ""}
                          placeholder=""
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <div className="border-t border-gray-800/70 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => append({ ...emptyItem })}
            className="inline-flex h-8 min-w-20 shrink-0 items-center justify-center whitespace-nowrap rounded-md border border-gray-600/90 bg-gray-900/80 px-2.5 text-xs font-medium text-gray-200 shadow-sm transition hover:border-gray-500 hover:bg-gray-800"
          >
            Add row
          </Button>
        </div>
      </div>
      {errors.items?.message && (
        <p className="mt-1.5 text-[11px] text-red-400">{errors.items.message}</p>
      )}

      <div className="mt-2 flex flex-wrap items-center justify-center gap-2 border-t border-gray-800/80 pt-3">
        <Button
          type="button"
          size="sm"
          disabled={saveMutation.isPending || (fuelReadingId != null && isLoadingDetail)}
          onClick={handleSubmit((d) => saveMutation.mutate(buildPayload(d)))}
        >
          {saveMutation.isPending ? "Saving…" : fuelReadingId != null ? "Update" : "Save"}
        </Button>
        {fuelReadingId != null && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-red-500/40 text-red-400"
            disabled={deleteMutation.isPending}
            onClick={() => setDeleteConfirmOpen(true)}
          >
            Delete
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={saveMutation.isPending}
          onClick={() => router.push("/transport/fuel-readings")}
        >
          Close
        </Button>
      </div>

      <Modal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          if (!deleteMutation.isPending) setDeleteConfirmOpen(false);
        }}
        className="max-w-md w-full"
      >
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Delete fuel reading?</h3>
          <p className="text-sm text-gray-400">This cannot be undone.</p>
          <div className="flex justify-end gap-2 border-t border-gray-800 pt-4">
            <Button type="button" variant="outline" size="sm" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
              onClick={() => fuelReadingId != null && deleteMutation.mutate(fuelReadingId)}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
